import { useState, useEffect, useCallback, useRef } from "react";
import { X, CheckCircle2, XCircle, Play, Download, FileImage } from "lucide-react";
import html2canvas from "html2canvas";
import { toast } from "sonner";

// Poppins font inject (sadece modal açıkken yüklenir, bir kez)
let poppinsLoaded = false;
function ensurePoppins() {
  if (poppinsLoaded || document.getElementById("poppins-font")) return;
  const link = document.createElement("link");
  link.id = "poppins-font";
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap";
  document.head.appendChild(link);
  // Also inject spin keyframe for video loader
  const style = document.createElement("style");
  style.textContent = "@keyframes spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(style);
  poppinsLoaded = true;
}

const OPTIONS = ["A", "B", "C", "D"];
const OPTION_KEYS = { A: "option_a", B: "option_b", C: "option_c", D: "option_d" };
const OPTION_IMG_KEYS = { A: "option_a_image_url", B: "option_b_image_url", C: "option_c_image_url", D: "option_d_image_url" };

function isVideoUrl(url) {
  // Also treat video URL fields that may have wrong extension (e.g. .png)
  // as video if they came from question_video_url field (handled by caller)
  return url && /\.(mp4|mov|webm|avi|mkv)(\?|$)/i.test(url);
}

// Route R2 video URLs through backend proxy to bypass pub-xxx.r2.dev streaming limits
// Route R2 video URLs through backend proxy to bypass pub-xxx.r2.dev streaming limits.
// Custom domain URLs (e.g. media.ehliyetdersi.net) are served directly — no proxy needed.
function getVideoProxySrc(url) {
  if (!url) return url;
  if (url.includes(".r2.dev/")) {
    const { BACKEND_URL_BASE } = require("../services/api");
    return `${BACKEND_URL_BASE}/api/media/video-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;  // custom domain → serve directly
}

function SimVideoPlayer({ src }) {
  const [status, setStatus] = useState("loading");
  if (!src) return null;
  const proxySrc = getVideoProxySrc(src);
  return (
    <div style={{ position: "relative", width: "100%", background: "#000", borderRadius: 8 }}>
      {status === "loading" && (
        <div style={{
          position: "absolute", inset: 0, display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.85)",
          zIndex: 10, borderRadius: 8,
        }}>
          <div style={{
            width: 28, height: 28, border: "2px solid rgba(255,255,255,0.2)",
            borderTopColor: "#fff", borderRadius: "50%",
            animation: "spin 0.8s linear infinite", marginBottom: 8,
          }} />
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, margin: 0 }}>Video yükleniyor...</p>
        </div>
      )}
      {status === "error" ? (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", height: 80,
          background: "rgba(220,50,50,0.08)", borderRadius: 8,
          border: "1px solid rgba(220,50,50,0.2)"
        }}>
          <p style={{ color: "#ef4444", fontSize: 12, margin: 0 }}>Video oynatılamadı</p>
        </div>
      ) : (
        <video
          key={proxySrc}
          src={proxySrc}
          controls
          autoPlay={false}
          muted
          loop={false}
          playsInline
          preload="metadata"
          style={{ maxWidth: "100%", maxHeight: 280, display: "block" }}
          onLoadStart={() => setStatus("loading")}
          onCanPlay={() => setStatus("ready")}
          onError={() => setStatus("error")}
        />
      )}
    </div>
  );
}

function OptionCard({ letter, question, state, onClick, answered }) {
  const text = question[OPTION_KEYS[letter]];
  const imgUrl = question[OPTION_IMG_KEYS[letter]];
  const isCorrect = question.correct_answer?.toUpperCase() === letter;

  let bg = "#ffffff";
  let textColor = "#111827";
  let borderColor = "#e5e7eb";
  let icon = null;
  let cursor = answered ? "default" : "pointer";

  if (answered) {
    if (state === letter) {
      // Selected option
      if (isCorrect) {
        bg = "#14b8a6"; textColor = "#ffffff"; borderColor = "#14b8a6";
        icon = <CheckCircle2 size={20} color="#ffffff" strokeWidth={2.5} />;
      } else {
        bg = "#ef4444"; textColor = "#ffffff"; borderColor = "#ef4444";
        icon = <XCircle size={20} color="#ffffff" strokeWidth={2.5} />;
      }
    } else if (isCorrect) {
      // Correct answer highlight when user was wrong
      bg = "#14b8a6"; textColor = "#ffffff"; borderColor = "#14b8a6";
      icon = <CheckCircle2 size={20} color="#ffffff" strokeWidth={2.5} />;
    }
  }

  return (
    <button
      data-testid={`sim-option-${letter}`}
      onClick={() => !answered && onClick(letter)}
      style={{
        width: "100%",
        background: bg,
        border: `1.5px solid ${borderColor}`,
        borderRadius: 8,
        padding: "14px 18px",
        display: "flex",
        alignItems: "center",
        gap: 14,
        cursor,
        transition: "all 0.2s",
        fontFamily: "Poppins, sans-serif",
        textAlign: "left",
      }}
    >
      {/* Letter badge */}
      <span style={{
        width: 28, height: 28, borderRadius: "50%",
        background: answered && (state === letter || isCorrect) ? "rgba(255,255,255,0.25)" : "#f3f4f6",
        color: answered && (state === letter || isCorrect) ? "#fff" : "#6b7280",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, flexShrink: 0,
      }}>
        {letter}
      </span>

      {/* Option content */}
      <span style={{ flex: 1, color: textColor, fontSize: 15, fontWeight: 500, lineHeight: 1.4 }}>
        {imgUrl ? (
          <img src={imgUrl} alt={`${letter} şık`}
            style={{ maxHeight: 80, maxWidth: "100%", objectFit: "contain", borderRadius: 6 }}
            loading="lazy" decoding="async"
          />
        ) : text ? (
          <span dangerouslySetInnerHTML={{ __html: text }} />
        ) : (
          <span style={{ color: "#9ca3af", fontStyle: "italic" }}>Boş şık</span>
        )}
      </span>

      {/* Result icon */}
      {icon && <span style={{ flexShrink: 0 }}>{icon}</span>}
    </button>
  );
}

function FeedbackBox({ isCorrect, explanation }) {
  return (
    <div
      data-testid="sim-feedback-box"
      style={{
        background: isCorrect ? "#f0fdfa" : "#fef2f2",
        border: `1.5px solid ${isCorrect ? "#14b8a6" : "#ef4444"}`,
        borderRadius: 8,
        padding: "14px 18px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {isCorrect ? (
        <CheckCircle2 size={20} color="#14b8a6" strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} />
      ) : (
        <XCircle size={20} color="#ef4444" strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} />
      )}
      <div>
        <p style={{ color: isCorrect ? "#0f766e" : "#dc2626", fontWeight: 600, fontSize: 15, margin: 0 }}>
          {isCorrect ? "Doğru!" : "Yanlış"}
        </p>
        {explanation && (
          <p style={{ color: "#4b5563", fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
            {explanation}
          </p>
        )}
      </div>
    </div>
  );
}

export default function QuestionSimulationModal({ question, onClose }) {
  const [selected, setSelected] = useState(null); // null | "A"|"B"|"C"|"D"
  const [answered, setAnswered] = useState(false);
  const [downloading, setDownloading] = useState(null); // null | "answered" | "blank"
  const captureRef = useRef(null); // sadece içerik alanını kapsar (header değil)

  // mode: "answered" = doğru cevap vurgulu, "blank" = cevapsız
  const downloadAsPng = async (mode = "answered") => {
    if (!captureRef.current) return;
    setDownloading(mode);

    const prevSelected = selected;
    const prevAnswered = answered;

    try {
      if (mode === "answered") {
        // Doğru cevabı geçici olarak göster
        setSelected(question.correct_answer);
        setAnswered(true);
        // CSS transition (200ms) + render buffer için bekle
        await new Promise((r) => setTimeout(r, 500));
      }

      // Cross-origin görselleri backend proxy'den geçir
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const imgs = Array.from(captureRef.current.querySelectorAll("img"));
      const originalSrcs = imgs.map((img) => img.src);

      await Promise.all(
        imgs.map(async (img) => {
          const src = img.src;
          if (!src || src.startsWith("data:")) return;
          try {
            const proxyUrl = `${API_URL}/api/media/proxy?url=${encodeURIComponent(src)}`;
            const res = await fetch(proxyUrl);
            const blob = await res.blob();
            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => resolve(e.target.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            img.src = dataUrl;
            await new Promise((r) => {
              if (img.complete) { r(); return; }
              img.onload = r;
              img.onerror = r;
              setTimeout(r, 800);
            });
          } catch { /* orijinal src ile devam et */ }
        })
      );

      const canvas = await html2canvas(captureRef.current, {
        useCORS: false,
        allowTaint: false,
        scale: 2,
        backgroundColor: "#f9fafb",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc, clonedElement) => {
          // Kırpılmayı önle: overflow/height kısıtlarını kaldır
          clonedElement.style.overflow = "visible";
          clonedElement.style.maxHeight = "none";
          clonedElement.style.height = "auto";

          // Tüm üst elementlerin overflow kısıtını da kaldır
          let parent = clonedElement.parentElement;
          while (parent && parent !== clonedDoc.body) {
            parent.style.overflow = "visible";
            parent.style.maxHeight = "none";
            parent = parent.parentElement;
          }

          // Bulanıklığı önle: tüm transition/animation'ı durdur
          const allEls = clonedElement.querySelectorAll("*");
          allEls.forEach((el) => {
            el.style.transition = "none";
            el.style.animation = "none";
          });
        },
      });

      imgs.forEach((img, i) => { img.src = originalSrcs[i]; });

      const suffix = mode === "answered" ? "_cevapli" : "_soru";
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `${question.question_code || "soru"}${suffix}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success(mode === "answered" ? "Cevaplı PNG indirildi" : "Cevapsız PNG indirildi");
    } catch {
      toast.error("PNG oluşturulamadı");
    } finally {
      if (mode === "answered") {
        setSelected(prevSelected);
        setAnswered(prevAnswered);
      }
      setDownloading(null);
    }
  };

  useEffect(() => {
    ensurePoppins();
    // ESC ile kapat
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // Reset on question change
  useEffect(() => {
    setSelected(null);
    setAnswered(false);
  }, [question?.id]);

  const handleSelect = useCallback((letter) => {
    setSelected(letter);
    setAnswered(true);
  }, []);

  if (!question) return null;

  const isCorrect = answered && question.correct_answer?.toUpperCase() === selected;
  const hasImage = question.question_image_url;
  const hasVideo = isVideoUrl(question.question_video_url);
  const hasGif = question.question_gif_url;

  // Progress bar: single question mode, show 1/1 visually (60% filled)
  const progressPct = answered ? 100 : 30;

  return (
    <div
      data-testid="sim-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "0 16px",
        overflowY: "auto",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div
        data-testid="sim-modal-card"
        style={{
          width: "100%", maxWidth: 560,
          margin: "32px auto",
          borderRadius: 16,
          background: "#f9fafb",
          boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* ── HEADER (captureRef dışında) ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px",
          background: "#ffffff",
          borderBottom: "1px solid #f3f4f6",
        }}>
          <button
            data-testid="sim-close-btn"
            onClick={onClose}
            style={{
              width: 36, height: 36, borderRadius: "50%",
              border: "none", background: "#f3f4f6",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#e5e7eb"}
            onMouseLeave={e => e.currentTarget.style.background = "#f3f4f6"}
          >
            <X size={16} color="#374151" strokeWidth={2.5} />
          </button>

          <span style={{ color: "#4b5563", fontSize: 14, fontWeight: 500 }}>
            <span style={{ color: "#14b8a6", fontWeight: 700 }}>Önizleme</span>
          </span>

          {/* İki indirme butonu */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {/* Cevapsız indir */}
            <button
              data-testid="sim-png-blank-btn"
              onClick={() => downloadAsPng("blank")}
              disabled={downloading !== null}
              title="Cevapsız indir"
              style={{
                width: 36, height: 36, borderRadius: "50%",
                border: "1.5px solid #e5e7eb",
                background: downloading === "blank" ? "#e5e7eb" : "#ffffff",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: downloading !== null ? "not-allowed" : "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => downloading === null && (e.currentTarget.style.background = "#f0fdfa")}
              onMouseLeave={e => (e.currentTarget.style.background = downloading === "blank" ? "#e5e7eb" : "#ffffff")}
            >
              {downloading === "blank"
                ? <div style={{ width: 14, height: 14, border: "2px solid #9ca3af", borderTopColor: "#14b8a6", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                : <FileImage size={15} color="#6b7280" strokeWidth={2} />}
            </button>

            {/* Cevaplı indir */}
            <button
              data-testid="sim-png-btn"
              onClick={() => downloadAsPng("answered")}
              disabled={downloading !== null}
              title="Cevaplı indir (doğru cevap gösterir)"
              style={{
                width: 36, height: 36, borderRadius: "50%",
                border: "none", background: downloading === "answered" ? "#e5e7eb" : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: downloading !== null ? "not-allowed" : "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => downloading === null && (e.currentTarget.style.background = "#f0fdfa")}
              onMouseLeave={e => (e.currentTarget.style.background = downloading === "answered" ? "#e5e7eb" : "transparent")}
            >
              {downloading === "answered"
                ? <div style={{ width: 14, height: 14, border: "2px solid #9ca3af", borderTopColor: "#14b8a6", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                : <Download size={16} color="#6b7280" strokeWidth={2} />}
            </button>
          </div>
        </div>

        {/* ── CAPTURE ALANI (header yok, butonlar yok) ── */}
        <div ref={captureRef} style={{ background: "#f9fafb" }}>

        {/* ── PROGRESS BAR ── */}
        <div style={{ height: 3, background: "#e5e7eb", position: "relative" }}>
          <div style={{
            height: "100%",
            width: `${progressPct}%`,
            background: "#14b8a6",
            transition: "width 0.4s ease",
            borderRadius: "0 2px 2px 0",
          }} />
        </div>

        {/* ── QUESTION CARD ── */}
        <div style={{
          margin: "20px 16px",
          background: "#ffffff",
          borderRadius: 12,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}>
          {/* Media area */}
          {(hasVideo || hasImage || hasGif) && (
            <div style={{
              background: "#f8fafc",
              display: "flex", justifyContent: "center", alignItems: "center",
              minHeight: 180, maxHeight: 300, overflow: "hidden",
            }}>
              {hasVideo ? (
                <SimVideoPlayer src={question.question_video_url} />
              ) : hasGif ? (
                <img src={question.question_gif_url} alt="gif"
                  style={{ maxWidth: "100%", maxHeight: 280, objectFit: "contain" }}
                  loading="lazy" />
              ) : (
                <img
                  src={question.question_image_url} alt="soru görseli"
                  style={{ maxWidth: "100%", maxHeight: 280, objectFit: "contain" }}
                  loading="eager"
                  onError={e => { e.target.style.display = "none"; }}
                />
              )}
            </div>
          )}

          {/* Question text */}
          <div style={{ padding: "24px 28px 20px 28px" }}>
            <div
              data-testid="sim-question-text"
              dangerouslySetInnerHTML={{ __html: question.question_text }}
              style={{
                color: "#111827", fontSize: 17, fontWeight: 700,
                lineHeight: 1.55, margin: 0,
                fontFamily: "Poppins, sans-serif",
                whiteSpace: "pre-wrap",
              }}
            />
          </div>
        </div>

        {/* ── OPTIONS ── */}
        <div style={{
          padding: "0 16px",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          {OPTIONS.map((letter) => (
            question[OPTION_KEYS[letter]] || question[OPTION_IMG_KEYS[letter]] ? (
              <OptionCard
                key={letter}
                letter={letter}
                question={question}
                state={selected}
                onClick={handleSelect}
                answered={answered}
              />
            ) : null
          ))}
        </div>

        {/* ── FEEDBACK ── */}
        {answered && (
          <div style={{ padding: "16px 16px 0 16px" }}>
            <FeedbackBox isCorrect={isCorrect} explanation={question.explanation} />
          </div>
        )}

        {/* ── NEXT BUTTON — indirme sırasında gizle ── */}
        {downloading === null && (
        <div style={{ padding: "16px 16px 24px 16px" }}>
          {answered ? (
            <button
              data-testid="sim-next-btn"
              onClick={onClose}
              style={{
                width: "100%", padding: "15px",
                background: "#FF8C42",
                border: "none", borderRadius: 8,
                color: "#ffffff", fontSize: 15, fontWeight: 600,
                cursor: "pointer",
                fontFamily: "Poppins, sans-serif",
                transition: "background 0.15s",
                boxShadow: "0 4px 14px rgba(255,140,66,0.35)",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#ff7a28"}
              onMouseLeave={e => e.currentTarget.style.background = "#FF8C42"}
            >
              Sonraki Soru
            </button>
          ) : (
            <div style={{ height: 50 }} /> /* Placeholder so layout doesn't jump */
          )}
        </div>
        )}
        </div> {/* captureRef kapanışı */}
      </div>  {/* modal card kapanışı */}
    </div>
  );
}

// Convenience trigger button for lists/tables
export function SimulationButton({ question, size = "sm" }) {
  const [open, setOpen] = useState(false);
  if (!question) return null;
  return (
    <>
      {size === "sm" ? (
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          data-testid={`sim-btn-${question.question_code}`}
          title="Simülasyonda Aç"
          style={{
            width: 28, height: 28, borderRadius: "50%",
            border: "none", background: "transparent",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", color: "#9ca3af", transition: "all 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.color = "#14b8a6"; e.currentTarget.style.background = "#f0fdfa"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.background = "transparent"; }}
        >
          <Play size={13} strokeWidth={2} />
        </button>
      ) : (
        <button
          onClick={() => setOpen(true)}
          data-testid={`sim-btn-${question.question_code}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            padding: "8px 16px",
            background: "#14b8a6",
            border: "none", borderRadius: 8,
            color: "#ffffff", fontSize: 14, fontWeight: 600,
            cursor: "pointer", fontFamily: "Poppins, sans-serif",
            transition: "background 0.15s",
            boxShadow: "0 2px 8px rgba(20,184,166,0.3)",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "#0d9488"}
          onMouseLeave={e => e.currentTarget.style.background = "#14b8a6"}
        >
          <Play size={14} strokeWidth={2} />
          Simülasyonda Aç
        </button>
      )}
      {open && (
        <QuestionSimulationModal question={question} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
